import classNames from 'classnames';
import { useScroll } from 'framer-motion';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import type { Thing } from 'schema-dts';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { TeamPerson } from '@/entities';
import { Day } from '@/entities/day';
import { Direction } from '@/entities/direction';
import { Document } from '@/entities/document';
import { Format } from '@/entities/format';
import { Place } from '@/entities/place';
import { AppCompaniesResponse, getCommonPageProps, getCompanies, getCompanyCategories } from '@/shared/api';
import { getAccordions } from '@/shared/api/accordions';
import { getDays } from '@/shared/api/days';
import { getDepartments } from '@/shared/api/departments';
import { getDirections } from '@/shared/api/directions';
import { getEmployees } from '@/shared/api/employees';
import { AppGetCommunityEventDetailResponse, getCommunityEventDetail } from '@/shared/api/get-event-detail';
import { getHistory } from '@/shared/api/history';
import { getFormats } from '@/shared/api/participation-formats';
import { getPeople } from '@/shared/api/people';
import { getPlaces } from '@/shared/api/places';
import { getPrivileges } from '@/shared/api/privileges';
import { ApiProgramRequest, AppProgramResponse, getProgram } from '@/shared/api/program';
import { getQuestions } from '@/shared/api/questions';
import { getReasons } from '@/shared/api/reasons';
import { getSchemes } from '@/shared/api/schemes';
import { getSelectionStages } from '@/shared/api/selection-stages';
import { getTickets } from '@/shared/api/tickets';
import { getVideo } from '@/shared/api/video';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import AboutSection, { aboutSectionId } from '@/shared/components/sections/AboutSection';
import { AboutSectionProps } from '@/shared/components/sections/AboutSection/AboutSection';
import AwardsSection, { AwardsSectionProps } from '@/shared/components/sections/AwardsSection';
import DirectionsSection, {
    DirectionsSectionProps,
    directionsSectionId,
    isVisibleDirection,
} from '@/shared/components/sections/DirectionsSection';
import FaqSection, { FaqSectionProps, faqSectionId } from '@/shared/components/sections/FaqSection';
import FullscreenVideoSection, {
    FullscreenVideoSectionProps,
} from '@/shared/components/sections/FullscreenVideoSection';
import HowItWasSection, { HowItWasSectionProps, howItWasSectionId } from '@/shared/components/sections/HowItWasSection';
import MissionSection, { MissionSectionProps } from '@/shared/components/sections/MissionSection';
import OrganizeSection, { OrganizeSectionProps, organizeSectionId } from '@/shared/components/sections/OrganizeSection';
import PageWelcome from '@/shared/components/sections/PageWelcome';
import ParticipantsSection, { ParticipantsSectionProps } from '@/shared/components/sections/ParticipantsSection';
import ProgramDetailSection from '@/shared/components/sections/ProgramDetailSection';
import { ProgramDetailSectionProps } from '@/shared/components/sections/ProgramDetailSection/ProgramDetailSection';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import SchemaSection, { SchemaSectionProps } from '@/shared/components/sections/SchemaSection';
import SelectionStagesSection, {
    SelectionStagesSectionProps,
    selectionStagesSectionId,
} from '@/shared/components/sections/SelectionStagesSection';
import TarifSection, { TarifSectionProps } from '@/shared/components/sections/TarifSection';
import TariffsInfoSection, {
    TariffsInfoSectionProps,
    tariffsInfoSectionId,
} from '@/shared/components/sections/TariffsInfoSection';
import TeamSection, { TeamSectionProps, teamSectionId } from '@/shared/components/sections/TeamSection';
import DocumentLink from '@/shared/components/shared/DocumentLink';
import ErrorMessage from '@/shared/components/shared/ErrorMessage';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import NavigationAnchors from '@/shared/components/shared/NavigationAnchors';
import Typografed from '@/shared/components/shared/Typografed';
import { Anchors, AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { formatDate, formatDateRange, formatLocalIso8601CalendarDate } from '@/shared/lib/utils/dates';
import { generateEventPageJsonLd } from '@/shared/lib/utils/event-page-json-ld';
import { filterFaqQuestionsVisibleInFaqSection } from '@/shared/lib/utils/faq-json-ld';

const CommunityEventPage = ({
    breadcrumbs,
    meta,
    footerData,
    h1,
    navigation,
    anchors,
    location,
    dates,
    aboutSectionProps,
    fullscreenVideoSectionProps,
    howItWasSectionProps,
    participantsSectionProps,
    missionSectionProps,
    organizeSectionProps,
    faqSectionProps,
    schemaSectionProps,
    teamSectionProps,
    expoQuestionsSectionProps,
    expoContactPersonProps,
    awardsSectionProps,
    selectionStagesSectionProps,
    tariffsInfoSectionProps,
    programDetailSectionProps,
    days,
    formats,
    places,
    directions,
    programInitialParams,
    initialProgramResponse,
    doc,
    programSectionTitle,
    programDocs,
    directionsSectionProps,
    displayMission,
    communityEventPath,
    eventStartDateIso,
    eventEndDateIso,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const ref = useRef(null);
    const theme = useHeaderThemeValue();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start -30%', 'end end'] });
    const [activeTabHash, setActiveTabHash] = useState(navigation[0].href);
    useIsomorphicLayoutEffect(() => {
        const activeNavItem = navigation.find((item) => item.href === window.location.hash);
        setActiveTabHash(activeNavItem?.href || navigation[0].href);
    }, [navigation]);

    return (
        <DefaultLayout>
            <section className="community__detail-section" ref={ref}>
                <PageWelcome
                    breadcrumbs={breadcrumbs}
                    title={h1}
                    document={doc}
                    aboveTitleSlot={
                        <div className="text-m community-event-date-location">
                            <Typografed>{[dates, location].filter(Boolean).join(' / ')}</Typografed>
                        </div>
                    }
                />
                <div className="wrapper">
                    <div className="participation-list text-l">
                        {navigation.map((navItem) => (
                            <button
                                key={navItem.id}
                                className={classNames('participation-list-item', {
                                    'is-active': navItem.href === activeTabHash,
                                })}
                                onClick={() => {
                                    setActiveTabHash(navItem.href);
                                    window.location.hash = navItem.href;
                                }}
                            >
                                {navItem.title}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="tab-content fade-in" hidden={activeTabHash !== '#about'}>
                    <NavigationAnchors
                        theme={theme}
                        navigation={navigation}
                        anchors={anchors.about}
                        scrollYProgress={scrollYProgress}
                        className="community-detail-anchors"
                    />

                    <ErrorBoundary fallback={<ErrorMessage />}>
                        <AboutSection
                            aboutSectionProps={aboutSectionProps}
                            className="js-header-theme-trigger"
                            data-theme="dark"
                        />
                    </ErrorBoundary>
                    <ErrorBoundary fallback={<ErrorMessage />}>
                        <FullscreenVideoSection data={fullscreenVideoSectionProps} data-theme="dark" />
                    </ErrorBoundary>

                    {howItWasSectionProps.slider.images.length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <HowItWasSection howItWasSectionProps={howItWasSectionProps} theme="dark" />
                        </ErrorBoundary>
                    )}

                    {participantsSectionProps.participantsSlider.length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <ParticipantsSection
                                id="participants"
                                participantsSectionProps={participantsSectionProps}
                                className="js-header-theme-trigger js-bg-trigger-dark"
                                data-bg="#f2f2f2"
                                data-theme="dark"
                            />
                        </ErrorBoundary>
                    )}

                    {missionSectionProps.cards.length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            {displayMission === 'with-image' ? (
                                <MissionSection id="mission" missionSectionProps={missionSectionProps} />
                            ) : displayMission === 'without-image' ? (
                                <TarifSection
                                    id="mission"
                                    tarifSectionProps={missionSectionPropsToTarifSectionProps(missionSectionProps)}
                                />
                            ) : null}
                        </ErrorBoundary>
                    )}
                    {awardsSectionProps.awards.length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <AwardsSection
                                awardsSectionProps={awardsSectionProps}
                                className="js-header-theme-trigger"
                                data-theme="dark"
                            />
                        </ErrorBoundary>
                    )}

                    {directionsSectionProps.directions.some(isVisibleDirection) && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <DirectionsSection directionsSectionProps={directionsSectionProps} />
                        </ErrorBoundary>
                    )}

                    {selectionStagesSectionProps.cards.length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <SelectionStagesSection
                                selectionStagesSectionProps={selectionStagesSectionProps}
                                className="js-header-theme-trigger"
                                data-theme="dark"
                            />
                        </ErrorBoundary>
                    )}

                    {tariffsInfoSectionProps.tariffs.length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <TariffsInfoSection
                                {...tariffsInfoSectionProps}
                                className="js-header-theme-trigger"
                                data-theme="dark"
                            />
                        </ErrorBoundary>
                    )}

                    {programDetailSectionProps.cards.length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <div id="program" className="program-block js-header-theme-trigger" data-theme="dark">
                                <div className="wrapper program-block__top">
                                    <h2 className="h3 program-block__title">
                                        <Typografed>{programSectionTitle}</Typografed>
                                    </h2>
                                    {programDocs.length > 0 && (
                                        <div className="program-block__link">
                                            {programDocs.map((doc, i) => (
                                                <DocumentLink key={i} document={doc} className="program-block__link" />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <ProgramDetailSection
                                    programDetailSectionProps={programDetailSectionProps}
                                    isButtonVisible={true}
                                    days={days}
                                    directions={directions}
                                    places={places}
                                    formats={formats}
                                    initialParams={programInitialParams}
                                    initialResponse={initialProgramResponse}
                                    visibleCardsCount={6}
                                    disableDirections
                                    mainTheme={h1}
                                    pdfTitle="Главная тема Форума TNF 2026 <br/> Нефтегазовая отрасль будущего: энергия лидерства"
                                />
                            </div>
                        </ErrorBoundary>
                    )}

                    {organizeSectionProps.categories.length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <OrganizeSection organizeSectionProps={organizeSectionProps} />
                        </ErrorBoundary>
                    )}

                    {teamSectionProps.departments.flatMap((department) => department.members).length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <TeamSection
                                teamSectionProps={teamSectionProps}
                                className="js-header-theme-trigger"
                                data-theme="dark"
                            />
                        </ErrorBoundary>
                    )}

                    {filterFaqQuestionsVisibleInFaqSection(faqSectionProps.questions).length > 0 && (
                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <FaqSection faqSectionProps={faqSectionProps} />
                        </ErrorBoundary>
                    )}
                </div>

                {schemaSectionProps.schemas.length > 0 && (
                    <div className="tab-content fade-in" hidden={activeTabHash !== '#expo'}>
                        <NavigationAnchors
                            theme={theme}
                            navigation={navigation}
                            anchors={anchors.exhibition}
                            scrollYProgress={scrollYProgress}
                            className="community-detail-anchors"
                        />

                        <ErrorBoundary fallback={<ErrorMessage />}>
                            <SchemaSection
                                id="schema-exhibition"
                                {...schemaSectionProps}
                                className="js-header-theme-trigger"
                                data-theme="dark"
                            />
                        </ErrorBoundary>

                        {(expoQuestionsSectionProps.title || expoQuestionsSectionProps.description) && (
                            <ErrorBoundary fallback={<ErrorMessage />}>
                                <QuestionsSection
                                    id="feedback-exhibition"
                                    questionsSectionProps={expoQuestionsSectionProps}
                                    contactPersonProps={expoContactPersonProps}
                                    className="js-header-theme-trigger"
                                    data-theme="dark"
                                />
                            </ErrorBoundary>
                        )}
                    </div>
                )}
            </section>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-community-event"
                data={generateEventPageJsonLd({
                    name: h1,
                    descriptionHtml: aboutSectionProps.description,
                    metaDescription: meta.description,
                    pagePath: communityEventPath,
                    startDateIso: eventStartDateIso ?? undefined,
                    endDateIso: eventEndDateIso ?? undefined,
                    offers: (tariffsInfoSectionProps.tariffs ?? []).map((tariff) => ({
                        name: tariff.title,
                        price: tariff.price,
                        url: tariff.button?.href,
                    })),
                    breadcrumbs,
                    faqQuestions: faqSectionProps.questions,
                    contacts: footerData,
                })}
            />
        </DefaultLayout>
    );
};

export default CommunityEventPage;

function missionSectionPropsToTarifSectionProps(props: MissionSectionProps): TarifSectionProps {
    return {
        title: props.title,
        subtitle: props.subtitle,
        button:
            props.link?.href != null && props.link.href !== ''
                ? { text: props.link.name ?? '', href: props.link.href }
                : null,
        cards: props.cards.map((card) => ({
            title: card.text,
            description: card.detail ?? '',
        })),
    };
}

type CommunityEventPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        navigation: AppNavigation;
        anchors: {
            about: Anchors;
            exhibition: Anchors;
            participation: Anchors;
        };
        location: string | null;
        dates: string;
        aboutSectionProps: AboutSectionProps;
        fullscreenVideoSectionProps: FullscreenVideoSectionProps;
        howItWasSectionProps: HowItWasSectionProps;
        participantsSectionProps: ParticipantsSectionProps;
        missionSectionProps: MissionSectionProps;
        programDetailSectionProps: ProgramDetailSectionProps;
        days: Day[];
        formats: Format[];
        places: Place[];
        directions: Direction[];
        programInitialParams: ApiProgramRequest;
        initialProgramResponse: AppProgramResponse;
        organizeSectionProps: OrganizeSectionProps;
        teamSectionProps: TeamSectionProps;
        schemaSectionProps: SchemaSectionProps;
        faqSectionProps: FaqSectionProps;
        expoQuestionsSectionProps: QuestionsSectionProps;
        participantQuestionsSectionProps: QuestionsSectionProps;
        expoContactPersonProps: TeamPerson | null;
        participantContactPersonProps: TeamPerson | null;
        awardsSectionProps: AwardsSectionProps;
        tariffsInfoSectionProps: TariffsInfoSectionProps;
        selectionStagesSectionProps: SelectionStagesSectionProps;
        doc: Document | null;
        programSectionTitle: string;
        programDocs: Document[];
        directionsSectionProps: DirectionsSectionProps;
        displayMission: string;
        communityEventPath: string;
        eventStartDateIso: string | null;
        eventEndDateIso: string | null;
    }
>;

export const getServerSideProps: GetServerSideProps<CommunityEventPageProps> = async ({ query }) => {
    const slug = query.eventSlug as string;
    const programInitialParams: ApiProgramRequest = {};

    const [eventDetailSettledResponse] = await Promise.allSettled([getCommunityEventDetail(slug)]);

    if (eventDetailSettledResponse.status === 'rejected') {
        const rejectedEventDetailSettledResponse = eventDetailSettledResponse as PromiseRejectedResult;
        if (rejectedEventDetailSettledResponse.reason?.response?.status === 404) {
            return { notFound: true };
        } else {
            throw new Error(rejectedEventDetailSettledResponse.reason?.response.statusText ?? 'Internal Server Error');
        }
    }

    const eventDetailResponse = (
        eventDetailSettledResponse as PromiseFulfilledResult<AppGetCommunityEventDetailResponse>
    ).value;
    const programOnlyHR = eventDetailResponse.data!.program_only_hr || false;

    const eventId = eventDetailResponse.data?.id ?? -1;

    const daysResponse = await getDays({ params: { event: eventId } });

    const days = daysResponse.data || [];
    const dayIds = days.map((day) => day.id);

    programInitialParams.days = dayIds;
    const [
        commonPageProps,
        [companyCategoriesResponse, companiesResponse],
        peopleResponse,
        historyResponse,
        questionsResponse,
        accordionsResponse,
        selectionStagesResponse,
        placesResponse,
        directionsResponse,
        formatsResponse,
        showreelResponse,
        reasonsResponse,
        [employeesResponse, departmentsResponse],
        schemesResponse,
        privilegesResponse,
        ticketsResponse,
    ] = await Promise.all([
        getCommonPageProps(),
        getCompanyCategories({ params: { event: eventId } }).then((companyCategoriesResponse) =>
            Promise.all([
                companyCategoriesResponse,
                (companyCategoriesResponse.data ?? []).length === 0
                    ? Promise.resolve<AppCompaniesResponse>({
                          status: 'success',
                          data: [],
                      })
                    : getCompanies({
                          params: { categories: (companyCategoriesResponse.data ?? []).map((category) => category.id) },
                      }),
            ]),
        ),
        getPeople({ params: { event: eventId } }),
        getHistory({ params: { event: eventId } }),
        getQuestions({ params: { event: eventId } }),
        getAccordions({ params: { event: eventId } }),
        getSelectionStages({ params: { event: eventId } }),
        getPlaces(),
        getDirections({
            params: programOnlyHR ? { from_hr: 1 } : undefined,
        }),
        getFormats({ params: { event: eventId } }),
        getVideo({ params: { event: eventId } }),
        getReasons({ params: { event: eventId } }),
        getEmployees({ params: { event: eventId } }).then((employeesResponse) =>
            Promise.all([
                employeesResponse,
                getDepartments({
                    params: { event: eventId },
                    employees: (employeesResponse.data ?? []).filter((employee) => employee.block === 'team'),
                }),
            ]),
        ),
        getSchemes({ params: { event: eventId } }),
        getPrivileges({ params: { event: eventId } }),
        getTickets({ params: { event: eventId } }),
    ]);

    const pageTitle = eventDetailResponse.data!.name;
    const directions = directionsResponse.data || [];

    const paramsDirections = directions.map((direction) => direction.id);

    if (programOnlyHR) {
        programInitialParams.directions = paramsDirections;
    }

    const programResponse: AppProgramResponse =
        dayIds.length > 0
            ? await getProgram({ params: programInitialParams })
            : {
                  status: 'success',
                  data: {
                      totalCount: 0,
                      items: [],
                  },
              };

    const content = eventDetailResponse.data!.content;

    const directionsSectionDirections = days.map((day) => ({
        date: formatDate(new Date(day.publishedAt), '.', false),
        title: day.name,
        description: day.description ?? '',
        dayId: day.id,
        publishedAt: day.publishedAt,
        endedAt: day.endedAt ?? '',
        image: day.image ?? { src: '', alt: '' },
    }));
    const hasVisibleDirections = directionsSectionDirections.some(isVisibleDirection);

    const aboutAnchors: Anchors = [
        { id: aboutSectionId, name: 'О мероприятии' },
        ...(reasonsResponse.data?.length
            ? [{ id: howItWasSectionId, name: content.history?.properties.title || 'Фотогалерея' }]
            : []),
        ...(peopleResponse.data?.length
            ? [{ id: 'participants', name: content.people?.properties.title || 'Участники' }]
            : []),
        ...(reasonsResponse.data?.length
            ? [{ id: 'mission', name: content['for-what']?.properties.title || 'Причины посетить' }]
            : []),
        ...(hasVisibleDirections
            ? [{ id: directionsSectionId, name: content.direction?.properties.title || 'Тематические треки' }]
            : []),
        ...(selectionStagesResponse.data?.length
            ? [
                  {
                      id: selectionStagesSectionId,
                      name: content['selection-stage']?.properties.title || 'Этапы отбора спикеров',
                  },
              ]
            : []),
        ...(ticketsResponse.data?.length
            ? [{ id: tariffsInfoSectionId, name: content.tariff?.properties.title || 'Тарифы' }]
            : []),
        ...(programResponse.data?.items?.length
            ? [{ id: 'program', name: content.program?.properties.title || 'Программа' }]
            : []),
        ...(companyCategoriesResponse.data?.length
            ? [{ id: organizeSectionId, name: content.company?.properties.title || 'Партнеры' }]
            : []),
        ...(employeesResponse.data?.length
            ? [{ id: teamSectionId, name: content.team?.properties.title || 'Наша команда' }]
            : []),
        ...(questionsResponse.data?.length
            ? [{ id: faqSectionId, name: content.question?.properties.title || 'Частые вопросы' }]
            : []),
    ];

    const schemas = schemesResponse.data || [];

    const expoQuestionsFormVisible =
        eventDetailResponse.data!.content['expo-form']?.properties.title ||
        eventDetailResponse.data!.content['expo-form']?.properties.description;

    const exhibitionAnchors: Anchors =
        schemas.length > 0
            ? [
                  { id: 'schema-exhibition', name: 'Схема выставки' },
                  ...(expoQuestionsFormVisible ? [{ id: 'feedback-exhibition', name: 'Задать вопрос' }] : []),
              ]
            : [];

    const participationQuestionsFormVisible =
        eventDetailResponse.data!.content['participant-form']?.properties.title ||
        eventDetailResponse.data!.content['participant-form']?.properties.description;

    const participationAnchors: Anchors = [
        { id: tariffsInfoSectionId, name: 'Тарифы для участников' }, // TODO: задинамить когда будет API
        ...(selectionStagesResponse.data?.length
            ? [{ id: selectionStagesSectionId, name: 'Этапы отбора спикеров' }]
            : []),
        ...(participationQuestionsFormVisible ? [{ id: 'feedback-participants', name: 'Задать вопрос' }] : []),
    ];

    const formats = formatsResponse.data || [];
    const places = placesResponse.data || [];

    return {
        props: {
            ...commonPageProps,
            communityEventPath: `/community/${slug}/`,
            eventStartDateIso: formatLocalIso8601CalendarDate(new Date(eventDetailResponse.data!.dateFrom)) || null,
            eventEndDateIso: formatLocalIso8601CalendarDate(new Date(eventDetailResponse.data!.dateTo)) || null,
            bodyClass: 'community-detail-page',
            meta: {
                ...commonPageProps.meta,
                ...(eventDetailResponse.data?.meta || {}),
                title: eventDetailResponse.data?.meta.title || pageTitle,
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Календарь мероприятий', href: '/community/' },
                { text: eventDetailResponse.data?.meta.title || pageTitle, href: `/community/${slug}/` },
            ],
            displayMission: eventDetailResponse.data!.displayMission || 'with-image',
            h1: pageTitle,
            doc: eventDetailResponse.data!.file,
            location: eventDetailResponse.data!.location,
            dates: formatDateRange(
                formatDate(new Date(eventDetailResponse.data!.dateFrom)),
                formatDate(new Date(eventDetailResponse.data!.dateTo)),
            ),
            programSectionTitle: eventDetailResponse.data!.content.program?.properties.title || 'Программа',
            programDocs: (eventDetailResponse.data!.content.program?.properties.documents ?? []).map((doc) => ({
                src: doc.src,
                title: doc.title,
                alt: doc.alt,
            })),

            navigation: [
                {
                    id: 0,
                    title: 'О мероприятии',
                    href: '#about',
                },
                ...(schemas.length > 0
                    ? [
                          {
                              id: 1,
                              title: 'Выставка',
                              href: '#expo',
                          },
                      ]
                    : []),
            ],
            anchors: {
                about: aboutAnchors,
                exhibition: exhibitionAnchors,
                participation: participationAnchors,
            },
            aboutSectionProps: {
                title: eventDetailResponse.data!.aboutEventTitle,
                description: eventDetailResponse.data!.aboutEvent || '',
                link: eventDetailResponse.data!.aboutLink
                    ? {
                          href: eventDetailResponse.data!.aboutLink || '',
                          text: eventDetailResponse.data!.aboutLinkText || '',
                      }
                    : null,
            },
            fullscreenVideoSectionProps: {
                video: showreelResponse.data?.video ?? [],
                mobileVideo: showreelResponse.data?.mobileVideo ?? [],
            },
            howItWasSectionProps: {
                slider: historyResponse.data ?? { images: [] },
                title: eventDetailResponse.data!.content.history?.properties.title || '',
                description: eventDetailResponse.data!.content.history?.properties.description || '',
                link: eventDetailResponse.data!.content.history?.properties.link
                    ? {
                          href: eventDetailResponse.data!.content.history?.properties.link,
                          name: eventDetailResponse.data!.content.history?.properties.link_text ?? '',
                      }
                    : null,
            },
            participantsSectionProps: {
                title: eventDetailResponse.data!.content.people?.properties.title || 'Спикеры и участники',
                participantsSlider: (peopleResponse.data ?? []).map((person) => ({
                    name: person.name,
                    job: person.job,
                    img: person.img,
                })),
            },
            missionSectionProps: {
                title: eventDetailResponse.data?.content['for-what']?.properties.title || '',
                subtitle: eventDetailResponse.data?.content['for-what']?.properties.description || '',
                link: eventDetailResponse.data?.content['for-what']?.properties.link
                    ? {
                          href: eventDetailResponse.data.content['for-what'].properties.link,
                          name: eventDetailResponse.data.content['for-what'].properties.link_text,
                      }
                    : null,
                cards: reasonsResponse.data ?? [],
            },
            programDetailSectionProps: {
                cards: programResponse.data?.items || [],
            },
            directions,
            formats,
            days,
            places,
            programInitialParams,
            initialProgramResponse: programResponse,
            organizeSectionProps: {
                title: eventDetailResponse.data!.content.company?.properties.title || 'Организаторы и партнеры',
                categories: (companyCategoriesResponse.data ?? []).map((category) => ({
                    title: category.name,
                    description: category.description ?? '',
                    items: (companiesResponse.data ?? []).filter((company) => company.categoryId === category.id),
                })),
            },
            teamSectionProps: {
                title: eventDetailResponse.data!.content.team?.properties.title || 'Наша команда',
                departments: departmentsResponse.data ?? [],
            },
            faqSectionProps: {
                title: eventDetailResponse.data!.content.question?.properties.title || 'Частые вопросы',
                questions: questionsResponse.data ?? [],
            },
            schemaSectionProps: {
                title: eventDetailResponse.data!.content.scheme?.properties.title || '',
                schemas,
                documents: (eventDetailResponse.data!.content.scheme?.properties.documents ?? []).map((doc) => ({
                    src: doc.src,
                    title: doc.title,
                    alt: doc.alt,
                })),
            },
            expoQuestionsSectionProps: {
                title: eventDetailResponse.data!.content['expo-form']?.properties.title || '',
                description: eventDetailResponse.data!.content['expo-form']?.properties.description || '',
            },
            participantQuestionsSectionProps: {
                title: eventDetailResponse.data!.content['participant-form']?.properties.title || '',
                description: eventDetailResponse.data!.content['participant-form']?.properties.description || '',
            },
            expoContactPersonProps: employeesResponse.data?.find((employee) => employee.block === 'expoForm') || null,
            participantContactPersonProps:
                employeesResponse.data?.find((employee) => employee.block === 'participantForm') || null,
            tariffsInfoSectionProps: {
                title: eventDetailResponse.data!.content.tariff?.properties.title || 'Тарифы',
                subtitle: eventDetailResponse.data!.content.tariff?.properties.description || '',
                link: eventDetailResponse.data?.content.tariff?.properties.link
                    ? {
                          href: eventDetailResponse.data.content.tariff.properties.link,
                          name: eventDetailResponse.data.content.tariff.properties.link_text,
                      }
                    : null,
                allTariffFeatures: privilegesResponse.data ?? [],
                tariffs: ticketsResponse.data ?? [],
                discounts: (ticketsResponse.data ?? []).map((ticket) => ({
                    tariffId: ticket.id,
                    price: ticket.price ?? '',
                })),
                oferta:
                    (ticketsResponse.data ?? []).length > 0 &&
                    eventDetailResponse.data!.content.tariff?.properties.file_link
                        ? {
                              src: eventDetailResponse.data!.content.tariff!.properties.file_link,
                              title: eventDetailResponse.data!.content.tariff!.properties.file_text ?? '',
                              alt: eventDetailResponse.data!.content.tariff!.properties.file_alt ?? '',
                          }
                        : null,
            },
            awardsSectionProps: {
                title: eventDetailResponse.data!.content.accordion?.properties.title || '',
                awards: accordionsResponse.data ?? [],
            },
            selectionStagesSectionProps: {
                title: eventDetailResponse.data!.content['selection-stage']?.properties.title || '',
                documents: (eventDetailResponse.data!.content['selection-stage']?.properties.documents ?? []).map(
                    (doc) => ({
                        src: doc.src,
                        title: doc.title,
                        alt: doc.alt,
                    }),
                ),
                cards: selectionStagesResponse.data ?? [],
            },
            directionsSectionProps: {
                title: eventDetailResponse.data!.content.direction?.properties.title || '',
                descr: eventDetailResponse.data!.content.direction?.properties.description || '',
                directions: directionsSectionDirections,
            },
        } satisfies CommunityEventPageProps,
    };
};
